import { IS_NOT_EMPTY } from 'cargo-chief-commons';
import { PLAN_NAMES } from '@domain/constants/app-variables';

import { ProductsService } from '@service/products/products.service';
import { UtilService } from '@service/util/util.service';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { environment } from '@environment/environment';

export interface HeaderMenu {
	DASHBOARD: boolean;
	PROJECTS: boolean;
	CAPACITY: boolean;
	NETWORK: boolean;
	SCORECARDS: boolean;
	BA_PERFORMANCE_METRICS_ENABLED: boolean;
	LOADS: boolean;
	PRICING: boolean;
	LANES: boolean;
	CUSTOMERS: boolean;
	PROJECTS_BOARD: boolean;
	QUICK_SEARCH: boolean;
	BIDS: boolean;
	CARRIER_NETWORK: boolean;
}

export const buildMenuOptions = (
	isPaymentView: boolean,
	showLogout: boolean,
	isFullWidthLayout: boolean,
	IS_CARRIERS_ENABLED: boolean,
	utilService: UtilService,
	productsService: ProductsService,
	accountSettings: AccountSettingsService
) => {
	let MENU_OPTIONS: HeaderMenu = {
		DASHBOARD: false,
		PROJECTS: false,
		CAPACITY: false,
		NETWORK: false,
		SCORECARDS: false,
		LOADS: false,
		BA_PERFORMANCE_METRICS_ENABLED: false,

		PRICING: false,
		LANES: false,
		CUSTOMERS: false,
		PROJECTS_BOARD: false,
		QUICK_SEARCH: false,
		BIDS: false,
		CARRIER_NETWORK: false,
	};

	MENU_OPTIONS.DASHBOARD = getDashboardMenu(isPaymentView, utilService);
	MENU_OPTIONS.PROJECTS = getProjectsMenu(isPaymentView, utilService, productsService);
	MENU_OPTIONS.CAPACITY = getCapacityMenu(
		isPaymentView,
		showLogout,
		isFullWidthLayout,
		utilService,
		productsService
	);
	MENU_OPTIONS.NETWORK = getNetworkMenu(IS_CARRIERS_ENABLED, utilService);
	MENU_OPTIONS.SCORECARDS = getScorecardsMenu(utilService, productsService, accountSettings);
	MENU_OPTIONS.BA_PERFORMANCE_METRICS_ENABLED = accountSettings.BA_PERFORMANCE_METRICS_ENABLED;
	MENU_OPTIONS.LOADS = getLoadsMenu(
		isPaymentView,
		showLogout,
		isFullWidthLayout,
		utilService,
		productsService,
		accountSettings
	);
	MENU_OPTIONS.PRICING = getPricingMenu(utilService, productsService);
	MENU_OPTIONS.LANES = getLanesMenu(utilService);
	MENU_OPTIONS.CUSTOMERS = getCustomersMenu(utilService, productsService, accountSettings);
	MENU_OPTIONS.PROJECTS_BOARD = getProjectsBoardMenu(utilService, productsService);
	MENU_OPTIONS.QUICK_SEARCH = getQuickSearchMenu(IS_CARRIERS_ENABLED, utilService);
	MENU_OPTIONS.BIDS = getBidsMenu(utilService, productsService);
	MENU_OPTIONS.CARRIER_NETWORK = getCarrierNetworkMenu(utilService);

	return MENU_OPTIONS;
};

function getDashboardMenu(isPaymentView, utilService: UtilService): boolean {
	return (
		!isPaymentView &&
		(utilService.isAccountTypeBroker() ||
			utilService.isAccountTypeShipper() ||
			utilService.isAccountTypeCarrier())
	);
}

function getProjectsMenu(
	isPaymentView,
	utilService: UtilService,
	productsService: ProductsService
): boolean {
	return (
		(utilService.isAccountTypeBroker() || utilService.isAccountTypeShipper()) &&
		productsService.PROJECT_CARRIERS &&
		!isPaymentView &&
		!utilService.isUserRoleAdminOrOwnerOnly()
	);
}

function getCapacityMenu(
	isPaymentView,
	showLogout,
	isFullWidthLayout,
	utilService: UtilService,
	productsService: ProductsService
): boolean {
	let plan: string = utilService.getUserPlanName();
	return (
		showLogout &&
		isFullWidthLayout &&
		productsService.CAPACITY_SEARCH &&
		(utilService.isAccountTypeBroker() || utilService.isAccountTypeShipper()) &&
		!isPaymentView &&
		!utilService.isUserRoleAdminOrOwnerOnly() &&
		IS_NOT_EMPTY(plan) &&
		plan.toLowerCase() != PLAN_NAMES.PRICING.toLowerCase()
	);
}

function getNetworkMenu(IS_CARRIERS_ENABLED, utilService: UtilService): boolean {
	return (
		IS_CARRIERS_ENABLED &&
		!utilService.isUserRoleAdminOrOwnerOnly() &&
		(utilService.isAccountTypeBroker() || utilService.isAccountTypeShipper())
	);
}

function getScorecardsMenu(
	utilService: UtilService,
	productsService: ProductsService,
	accountSettings: AccountSettingsService
): boolean {
	return (
		(utilService.isAccountTypeBroker() || utilService.isAccountTypeShipper()) &&
		environment.SCORECARDS &&
		productsService.SCORECARDS &&
		accountSettings.SCORECARDS
	);
}

function getLoadsMenu(
	isPaymentView,
	showLogout,
	isFullWidthLayout,
	utilService: UtilService,
	productsService: ProductsService,
	accountSettings: AccountSettingsService
): boolean {
	return (
		showLogout &&
		isFullWidthLayout &&
		!isPaymentView &&
		(utilService.isAccountTypeBroker() ||
			utilService.isAccountTypeShipper() ||
			utilService.isUserRoleAdminOrOwnerOnly()) &&
		productsService.BOOKING_ASSISTANT &&
		accountSettings.BOOKING_ASSISTANT
	);
}

function getPricingMenu(utilService: UtilService, productsService: ProductsService): boolean {
	let plan: string = utilService.getUserPlanName();
	return (
		(IS_NOT_EMPTY(plan) && plan.toLowerCase() == PLAN_NAMES.PRICING.toLowerCase()) ||
		(utilService.isAccountTypeCarrier() &&
			productsService.PRICING &&
			productsService.RECENT_TRANSACTIONS)
	);
}

function getLanesMenu(utilService: UtilService): boolean {
	return utilService.isAccountTypeCarrier() && environment.CARRIER_LANES_VIEW;
}

function getCustomersMenu(
	utilService: UtilService,
	productsService: ProductsService,
	accountSettings: AccountSettingsService
): boolean {
	return (
		utilService.isAccountTypeBroker() &&
		environment.CUSTOMERS_VIEW &&
		productsService.CUSTOMERS &&
		accountSettings.CUSTOMERS &&
		accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS
	);
}

function getProjectsBoardMenu(utilService: UtilService, productsService: ProductsService): boolean {
	return (
		environment.PROJECTS_BOARD &&
		utilService.isAccountTypeCarrier() &&
		productsService.PROJECT_CARRIERS
	);
}

function getQuickSearchMenu(IS_CARRIERS_ENABLED, utilService: UtilService): boolean {
	return (
		(IS_CARRIERS_ENABLED && !utilService.isUserRoleAdminOrOwnerOnly()) ||
		utilService.isAccountTypeCarrier()
	);
}

function getBidsMenu(utilService: UtilService, productsService: ProductsService): boolean {
	return utilService.isAccountTypeCarrier() && productsService.BOOKING_ASSISTANT;
}

function getCarrierNetworkMenu(utilService: UtilService): boolean {
	return utilService.isAccountTypeCarrier();
}
